import { Mutex } from "async-mutex";
import { clearAuthTokens, getAccessToken, setAuthTokens } from "../utils/authTokens";
import { SERVER_URL } from "../config/env";

const BACKEND_AUTH_BASE = SERVER_URL ? `${SERVER_URL}/api/auth` : "/api/auth";

// centralized token refresh service with proactive refresh and rotation support
type BffRefreshOutcome =
  | { kind: "ok"; tokens: TokenResponse }
  | { kind: "transient" }
  | { kind: "definitive" };

class TokenService {
  private refreshMutex = new Mutex();
  private refreshPromise: Promise<TokenResponse | null> | null = null;
  /** set when the last performRefresh returned null — used by refreshSilent to avoid session-expired on 503 */
  private lastRefreshFailure: "transient" | "definitive" | null = null;

  // check if token is expiring soon (within 60 seconds)
  isTokenExpiringSoon(token: string, bufferSeconds = 60): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (!payload.exp) {
        // no expiry = not expiring
        return false;
      }
      const expiryTime = payload.exp * 1000; // convert to milliseconds
      const bufferTime = bufferSeconds * 1000;
      return Date.now() >= expiryTime - bufferTime;
    } catch (error) {
      console.warn("[TokenService] Error parsing token, treating as not expiring:", error);
      // if we can't parse it, don't trigger refresh - let the server decide
      return false;
    }
  }

  // check if token is expired or expiring soon
  isTokenExpired(token: string | null, bufferSeconds = 0): boolean {
    if (!token) {
      return true;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (!payload.exp) {
        return false; // no expiry = not expired
      }

      const expiryTime = payload.exp * 1000; // convert to milliseconds
      const bufferTime = bufferSeconds * 1000;
      return Date.now() >= expiryTime - bufferTime;
    } catch (error) {
      console.warn("[TokenService] Error parsing token, treating as expired:", error);
      return true; // if we can't parse it, treat as expired
    }
  }

  // get a valid access token, refreshing if necessary
  // this does NOT trigger session-expired events - it just returns null if no token
  async getValidAccessToken(): Promise<string | null> {
    const token = getAccessToken();

    if (!token) {
      // no token = user is not logged in, this is fine for public pages
      return null;
    }

    // proactive refresh: if token is expiring soon, refresh it
    if (this.isTokenExpiringSoon(token)) {
      console.log("[TokenService] Token expiring soon, proactively refreshing...");
      const tokens = await this.refreshSilent();
      if (tokens?.accessToken) return tokens.accessToken;
      // transient BFF failure: keep using existing token until next attempt
      if (this.lastRefreshFailure === "transient") {
        return token;
      }
      return null;
    }

    return token;
  }

  /**
   * single-flight refresh: mutex + shared promise so parallel callers (many axios 401s,
   * RTK + apiClient, AOTD /info + /comments, etc.) do not hit POST /api/auth/refresh concurrently.
   * concurrent OAuth refresh with rotation invalidates the refresh token — losers get invalid_grant
   * and the BFF may delete the session (routes/auth POST /refresh).
   */
  private async refreshTokensSingleFlight(): Promise<TokenResponse | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const release = await this.refreshMutex.acquire();

    try {
      if (this.refreshPromise) {
        return await this.refreshPromise;
      }

      // after acquiring the mutex, re-read the token — another caller may have refreshed it
      // while we waited. use a small buffer (5s) so we only skip the refresh when the stored
      // token is genuinely fresh, not when it's the same nearly-expired token that triggered us.
      const currentToken = getAccessToken();
      if (currentToken && !this.isTokenExpiringSoon(currentToken, 5)) {
        console.log("[TokenService] Token already refreshed by another request");
        return { accessToken: currentToken, refreshToken: "" };
      }

      console.log("[TokenService] Refreshing token (single-flight)...");
      this.refreshPromise = this.performRefresh();
      try {
        return await this.refreshPromise;
      } finally {
        this.refreshPromise = null;
      }
    } finally {
      release();
    }
  }

  // silent refresh - emits session-expired events only when refresh token is invalid/expired
  // used for proactive refresh where we don't want to disrupt the user
  // refresh token is in HttpOnly cookie, sent automatically with credentials: 'include'
  async refreshSilent(): Promise<TokenResponse | null> {
    this.lastRefreshFailure = null;
    try {
      const tokens = await this.refreshTokensSingleFlight();
      if (tokens) {
        setAuthTokens(tokens);
        console.log("[TokenService] Silent refresh successful");
        this.notifyRefreshSuccess(tokens.accessToken);
        return tokens;
      }

      if (this.lastRefreshFailure === "transient") {
        console.log("[TokenService] Silent refresh failed (transient BFF/auth), not emitting session-expired");
        return null;
      }

      console.log("[TokenService] Silent refresh failed - refresh token invalid/expired, emitting session-expired event");
      this.handleSessionExpired();
    } catch (error) {
      console.warn("[TokenService] Silent refresh threw an error:", error);
      console.log("[TokenService] Silent refresh error - emitting session-expired event");
      this.handleSessionExpired();
    }
    return null;
  }

  // refresh the access token using refresh token (from HttpOnly cookie)
  // this CAN emit session-expired events if the refresh fails
  async refresh(): Promise<TokenResponse | null> {
    this.lastRefreshFailure = null;
    try {
      const tokens = await this.refreshTokensSingleFlight();

      if (tokens) {
        setAuthTokens(tokens);
        console.log("[TokenService] Token refresh successful");
        this.notifyRefreshSuccess(tokens.accessToken);
        return tokens;
      }

      if (this.lastRefreshFailure === "transient") {
        console.log("[TokenService] Token refresh failed (transient), not emitting session-expired");
        return null;
      }

      console.log("[TokenService] Token refresh failed - session expired");
      this.handleSessionExpired();
      return null;
    } catch (error) {
      console.error("[TokenService] Token refresh error:", error);
      this.handleRefreshError(error);
      return null;
    }
  }

  private async resolveBffRefresh(): Promise<BffRefreshOutcome> {
    try {
      const response = await fetch(`${BACKEND_AUTH_BASE}/refresh`, {
        method: "POST",
        credentials: "include",
      });

      if (response.status === 503) {
        console.warn("[TokenService] BFF refresh returned 503 (transient)");
        return { kind: "transient" };
      }

      if (!response.ok) {
        return { kind: "definitive" };
      }

      const data = await response.json();
      if (!data?.authenticated || !data?.accessToken) {
        return { kind: "definitive" };
      }

      return {
        kind: "ok",
        tokens: {
          accessToken: data.accessToken,
          refreshToken: "",
        },
      };
    } catch (error) {
      console.warn("[TokenService] BFF refresh network error:", error);
      return { kind: "transient" };
    }
  }

  // perform the actual refresh API call via backend BFF
  private async performRefresh(): Promise<TokenResponse | null> {
    try {
      console.log("[TokenService] Refreshing via backend BFF session...");
      const outcome = await this.resolveBffRefresh();
      if (outcome.kind === "ok") {
        return outcome.tokens;
      }
      this.lastRefreshFailure = outcome.kind;
      return null;
    } catch (error) {
      console.error("[TokenService] Network error during refresh:", {
        error: error,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      this.lastRefreshFailure = "transient";
      return null;
    }
  }

  // handle session expiry
  private handleSessionExpired() {
    console.log("[TokenService] Handling session expired - clearing tokens and dispatching event");
    clearAuthTokens();
    // dispatch custom event for app-wide handling
    const event = new CustomEvent("session-expired", {
      detail: { reason: "refresh_failed" },
    });
    console.log("[TokenService] Dispatching session-expired event");
    window.dispatchEvent(event);
    console.log("[TokenService] Session-expired event dispatched");
  }

  // handle refresh errors
  private handleRefreshError(error: unknown) {
    clearAuthTokens();
    window.dispatchEvent(
      new CustomEvent("session-expired", {
        detail: { reason: "refresh_error", error },
      })
    );
  }

  // notify successful refresh
  private notifyRefreshSuccess(accessToken: string) {
    window.dispatchEvent(new CustomEvent("token-refreshed", {
      detail: { accessToken },
    }));
  }

  // clear all tokens and notify
  logout() {
    clearAuthTokens();
    window.dispatchEvent(
      new CustomEvent("session-expired", {
        detail: { reason: "logout" },
      })
    );
  }
}

// token response interface
export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

// singleton instance
export const tokenService = new TokenService();
